import React from 'react';
import { Link } from '@inertiajs/react';
import { Card, Button } from '~/marketing/ui';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { LegalPage, Section, P, UL } from '~/marketing/LegalPage';
import { legal } from '~/marketing/legalData';
import { routes } from '~/marketing/routes';

/* One address, no form, and no phone number. A form here would mean a
   controller, a mailer and a spam problem to deliver a message to the address
   already printed on the page, so the mailto is the whole feature; and
   self-serve is a standing principle, so there is no number to publish.

   **Neither absence is explained on the page, on purpose.** Until 2026-10-05 a
   section called "No phone, and no demo" said so, which denied a thing the
   product never does in order to refute it, and disclosed how small the company
   is on the page where a buyer decides whether it will still be here next year.
   Its replacement, "Email is the channel", made the same argument in the
   positive and went in the 2026-10-23 rewrite for the same reason: it answered
   a question the reader did not ask. **Do not restore either.** What replaced
   it points somewhere useful instead — the FAQ, the methodology page and the
   free check.

   **Rewritten 2026-10-23 against the tone rules, on the founder's review.**
   The lede "Spruce is operated by Spruce AEO. Everything below reaches the same
   inbox." named one business twice and told the reader nothing the card did
   not; it now says what to write about. "Trade" became "industry", because the
   product accepts any local business. The question item stopped promising
   that a suggestion "improves it for everyone in that trade": changing the
   shared questions does not change a business's existing set, so it says only
   that it helps. And the free check's report is no longer "yours to keep" —
   false since free checks began being deleted after twelve months on
   2026-10-12. The page carries no date: it is not a policy, and a date only
   suggested it changes often.

   **One sentence waits on a measurement.** This page used to say Stripe "is the
   name that appears on your statement and issues your invoice", which nobody
   had checked — the sandbox invoice was issued under Link. It comes back, worded
   from what the live test purchase shows. */

export default function Contact() {
  return (
    <LegalPage
      title="Contact"
      path={routes.contact}
      updated={null}
      description="How to reach Spruce about an account, a bill, a privacy request or a measurement you want explained. One address, and what to use it for."
      lede="Write to us about your account, a bill, your data, or a measurement you’d like explained."
    >
      <Card variant="signal" pad="lg">
        <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
          <p style={{ fontSize: 'var(--fs-h3)', fontWeight: 600, margin: 0 }}>
            <a href={`mailto:${legal.supportEmail}`}>{legal.supportEmail}</a>
          </p>
          <P>We aim to reply within two business days.</P>
          <div>
            <Button variant="secondary" size="sm" href={`mailto:${legal.supportEmail}`}>
              Email us
            </Button>
          </div>
        </div>
      </Card>

      <Section title="What to write about">
        <UL>
          <li>
            <strong>Your account or a measurement.</strong> If something isn’t working or a figure
            doesn’t look right, tell us which business it’s about and what you expected to see.
          </li>
          <li>
            <strong>A bill.</strong> Write from the address on your account and tell us which charge
            it’s about, so we can find it straight away. Our{' '}
            <Link href={routes.refund}>Refund Policy</Link> explains how refunds and cancellations
            work.
          </li>
          <li>
            <strong>Your data.</strong> Ask for a copy, a correction or a deletion. Our{' '}
            <Link href={routes.privacy}>Privacy Policy</Link> explains what we keep and for how
            long. You can also delete your account yourself from your{' '}
            <Link href={routes.account}>Account page</Link>.
          </li>
          <li>
            <strong>A question that doesn’t fit your business.</strong> Tell us which one, and how your
            customers would ask it. It helps us improve the questions for your industry.
          </li>
        </UL>
      </Section>

      <Section title="Answers you can read now">
        <P>
          How the figures are counted is covered in the <Link href={routes.faq}>FAQ</Link> and on
          the <Link href={routes.methodology}>Methodology page</Link>, and what each plan includes
          is covered on the <Link href={routes.pricing}>Pricing page</Link>. To see what {legal.product}{' '}
          measures before you sign up, <Link href={routes.grader}>run the free check</Link> on
          your own business.
        </P>
      </Section>

      {/* What a payment reviewer reads: who the business is and what it sells. */}
      <Section title="The business">
        <P>
          {legal.business} is a United States business at {legal.domain}. It sells subscription
          software that measures how AI assistants describe and recommend local businesses.
          Payments are processed by {legal.mor} as merchant of record.
        </P>
      </Section>
    </LegalPage>
  );
}

Contact.layout = withMarketingLayout;
